import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Knowledge Base API',
  content: `
The Knowledge Base API provides full CRUD access to Ellie's knowledge base — the collection of articles and folders that Ellie consults when answering questions. Use these endpoints to programmatically manage knowledge base content from external systems, automation scripts, or other agents.

All endpoints are under \`/api/v1/knowledge-base-items\` and require a valid Bearer token (see [Pliable API](/api-docs/pliable-api) for authentication).

---

## Data Model

Knowledge base items are either **folders** (organizational containers) or **articles** (content with a body).

| Field | Type | Description |
|---|---|---|
| \`id\` | string | Unique item ID. |
| \`item_type\` | string | \`"folder"\` or \`"article"\`. |
| \`name\` | string | Display name / title. |
| \`parent_id\` | string \\| null | ID of the parent folder. \`null\` for root-level items. |
| \`body\` | string \\| null | Article content (max 20,000 characters). \`null\` for folders. |
| \`tags\` | string[] | Tags for categorization and search. |
| \`status\` | string | \`"active"\` or \`"pending"\`. Pending items require approval before Ellie uses them. |
| \`proposed_by\` | string \\| null | Who proposed a pending item (\`"ellie"\` when Ellie auto-generates). |
| \`created_by\` | string | User ID of the creator. |
| \`updated_by\` | string | User ID of the last editor. |
| \`created_at\` | string | ISO 8601 creation timestamp. |

---

## Create an Item

\`\`\`
POST /api/v1/knowledge-base-items
\`\`\`

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/knowledge-base-items" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "item_type": "article",
    "name": "Monthly Sales KPIs",
    "body": "Total Sales, Units Sold, and Average Order Value are the primary KPIs tracked monthly...",
    "tags": ["sales", "kpis"],
    "parent_id": null
  }'
\`\`\`

| Field | Type | Required | Description |
|---|---|---|---|
| \`item_type\` | string | Yes | \`"folder"\` or \`"article"\`. |
| \`name\` | string | Yes | Display name. |
| \`body\` | string | No | Article content. Max 20,000 characters. |
| \`tags\` | string[] | No | Tags for categorization. |
| \`parent_id\` | string | No | Parent folder ID. Must reference an existing folder. |
| \`status\` | string | No | Defaults to \`"active"\`. Set to \`"pending"\` to require approval. |

### Response

\`\`\`json
{
  "record": {
    "id": "667a...",
    "item_type": "article",
    "name": "Monthly Sales KPIs",
    "body": "Total Sales, Units Sold, and Average Order Value are the primary KPIs tracked monthly...",
    "tags": ["sales", "kpis"],
    "parent_id": null,
    "status": "active",
    "created_by": "user_123",
    "updated_by": "user_123",
    "created_at": "2026-07-09T15:00:00Z"
  }
}
\`\`\`

---

## List Items

\`\`\`
GET /api/v1/knowledge-base-items
\`\`\`

Returns a paginated list of knowledge base items.

| Parameter | Type | Description |
|---|---|---|
| \`page\` | query | Page number (default 1). |
| \`limit\` | query | Items per page (default 25). |
| \`sort\` | query | Sort field (e.g. \`"name"\`, \`"-created_at"\` for descending). |
| \`filter\` | query | JSON filter object (e.g. \`{"item_type": "article", "status": "active"}\`). |
| \`search\` | query | Text search across item names. |

### Response

\`\`\`json
{
  "records": [...],
  "total": 42,
  "page": 1,
  "limit": 25
}
\`\`\`

---

## Get a Single Item

\`\`\`
GET /api/v1/knowledge-base-items/{id}
\`\`\`

### Response

\`\`\`json
{
  "record": {
    "id": "667a...",
    "item_type": "article",
    "name": "Monthly Sales KPIs",
    "body": "Total Sales, Units Sold, and...",
    "tags": ["sales", "kpis"],
    "parent_id": null,
    "status": "active"
  }
}
\`\`\`

---

## Update an Item

\`\`\`
PATCH /api/v1/knowledge-base-items/{id}
\`\`\`

Pass only the fields you want to change.

\`\`\`bash
curl -X PATCH "https://app.pliable.co/api/v1/knowledge-base-items/667a..." \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "body": "Updated article content...",
    "tags": ["sales", "kpis", "monthly"]
  }'
\`\`\`

---

## Delete an Item

\`\`\`
DELETE /api/v1/knowledge-base-items/{id}
\`\`\`

Deleting a folder cascades — all descendant folders and articles are also deleted. Any pending changes referencing deleted items are cleaned up automatically.

---

## Get Tree

\`\`\`
GET /api/v1/knowledge-base-items/tree
\`\`\`

Returns all items (up to 5,000) without the \`body\` field, for rendering a folder tree.

### Response

\`\`\`json
{
  "records": [
    { "id": "...", "item_type": "folder", "name": "Sales", "parent_id": null },
    { "id": "...", "item_type": "article", "name": "Monthly KPIs", "parent_id": "..." }
  ]
}
\`\`\`

---

## Search

\`\`\`
GET /api/v1/knowledge-base-items/search?q=sales
\`\`\`

Searches active articles by name, tags, and body content. Returns a 200-character body preview.

| Parameter | Type | Description |
|---|---|---|
| \`q\` | query | Search query (regex match against name, tags, and body). |

---

## Move an Item

\`\`\`
POST /api/v1/knowledge-base-items/{id}/move
\`\`\`

Moves an item to a different parent folder. Prevents circular references.

\`\`\`bash
curl -X POST "https://app.pliable.co/api/v1/knowledge-base-items/667a.../move" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -d '{
    "parent_id": "668b..."
  }'
\`\`\`

---

## Approval Workflow

Ellie can propose new articles or updates that require human approval before becoming active.

### Approve a Pending Item

\`\`\`
POST /api/v1/knowledge-base-items/{id}/approve
\`\`\`

Activates a pending item. If the pending item is an update to an existing article (\`original_item_id\` is set), the original is updated with the pending item's content and the pending copy is deleted.

### Reject a Pending Item

\`\`\`
POST /api/v1/knowledge-base-items/{id}/reject
\`\`\`

Deletes the pending item without applying changes.

### Mark as Pending

\`\`\`
POST /api/v1/knowledge-base-items/{id}/mark-pending
\`\`\`

Changes an active item's status to pending, removing it from Ellie's active knowledge until re-approved.

---

## Tags

\`\`\`
GET /api/v1/knowledge-base-items/tags
\`\`\`

Returns all distinct tags across active items.

\`\`\`json
{
  "tags": ["sales", "kpis", "onboarding", "data-quality"]
}
\`\`\`
`,
};

export default doc;
